import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { packRecipeCount } from './packRecipeCount.js';

const TOAST_DURATION_MS = 10000;

/**
 * PackToast — confirmation toast shown after adding a pack.
 * Uses the shared app toast design-system classes.
 */
export default function PackToast({
  pack,
  addedCount,
  refreshedCount = 0,
  error,
  onDismiss,
  onUndo,
  undoing = false,
  canUndo = false,
}) {
  useEffect(() => {
    if (undoing) return undefined;
    const t = setTimeout(onDismiss, TOAST_DURATION_MS);
    return () => clearTimeout(t);
  }, [onDismiss, undoing]);

  const packCount = packRecipeCount(pack);
  const refreshed = Number(refreshedCount) || 0;
  const added = Number(addedCount) || 0;
  const batchCount = added > 0 ? added : packCount;
  const batchLabel = `${batchCount} ${batchCount === 1 ? 'recipe' : 'recipes'}.`;
  const label = error
    ? error
    : refreshed > 0 && added === 0
      ? `${refreshed} recipe${refreshed === 1 ? '' : 's'} updated with the latest directions.`
      : refreshed > 0 && added > 0
        ? `${added} added, ${refreshed} updated with the latest directions.`
        : added > 0 && added < packCount
          ? `${added} added, ${packCount - added} already with your recipes`
          : batchLabel;

  return createPortal(
    <div className="app-toast-anchor">
      <div
        className={`app-toast app-toast--${error ? 'error' : 'success'}`}
        role={error ? 'alert' : 'status'}
        aria-live={error ? 'assertive' : 'polite'}
      >
        <span className="app-toast__icon" aria-hidden="true">{error ? '!' : '✓'}</span>
        <div className="app-toast__body">
          <div className="app-toast__title">
            {error ? `${pack.name} not added` : refreshed > 0 && added === 0 ? `${pack.name} updated` : `${pack.name} added`}
          </div>
          <div className="app-toast__text">{label}</div>
        </div>
        {!error && canUndo && (
          <button type="button" className="app-toast__action" onClick={onUndo} disabled={undoing}>
            {undoing ? 'Undoing...' : 'Undo'}
          </button>
        )}
        <button
          type="button"
          className="app-toast__dismiss"
          aria-label="Dismiss notification"
          onClick={onDismiss}
        >
          ×
        </button>
      </div>
    </div>,
    document.body
  );
}
